import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig, STAGE_COLORS, STAGE_COLOR_CLASSES } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, Lock } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { label_id: "", label_en: "", probability: 20, color: "slate", active: true };

export const StagesAdmin = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const err = (e) => toast.error(e?.response?.data?.detail || "Error");

  const save = async () => {
    if (!form.label_id || !form.label_en) { toast.error(t("stage_labels_required")); return; }
    try {
      const payload = { ...form, probability: Number(form.probability) || 0 };
      if (editing) await api.put(`/config/stages/${editing.key}`, payload);
      else await api.post("/config/stages", payload);
      toast.success(t("stage_saved"));
      setOpen(false);
      await cfg.reload();
    } catch (e) { err(e); }
  };

  const move = async (key, direction) => {
    try {
      await api.put(`/config/stages/${key}/order?direction=${direction}`);
      await cfg.reload();
    } catch (e) { err(e); }
  };

  const del = async (s) => {
    if (!window.confirm(`${t("delete")} ${s.label_id}?`)) return;
    try {
      await api.delete(`/config/stages/${s.key}`);
      toast.success(t("stage_deleted"));
      await cfg.reload();
    } catch (e) { err(e); }
  };

  return (
    <div className="space-y-4" data-testid="stages-admin">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("stages_desc")}</p>
        <Button data-testid="add-stage-button" className="h-9 rounded-full bg-orange-500 hover:bg-orange-600 gap-2"
          onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}>
          <Plus className="w-4 h-4" /> {t("add_stage")}
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden shadow-sm rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-3">{t("stage")}</th>
              <th className="px-4 py-3">EN</th>
              <th className="px-4 py-3 text-right">%</th>
              <th className="px-4 py-3">{t("status")}</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(cfg?.stages || []).map((s, idx) => {
              const style = STAGE_COLOR_CLASSES[s.color] || STAGE_COLOR_CLASSES.slate;
              return (
                <tr key={s.key} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`stage-row-${s.key}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${style.bg} ${style.border} ${style.text}`}>
                      <span className={`stage-dot ${style.dot}`}></span>{s.label_id}
                    </span>
                    {s.locked && <Lock className="w-3 h-3 inline ml-2 text-slate-300" />}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{s.label_en}</td>
                  <td className="px-4 py-3 text-right mono text-slate-700">{s.probability}%</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={s.active ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                      {t(s.active ? "active" : "inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => move(s.key, "up")} disabled={idx === 0} data-testid={`stage-up-${s.key}`}
                      className="p-1.5 text-slate-400 hover:text-slate-900 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(s.key, "down")} disabled={idx === cfg.stages.length - 1} data-testid={`stage-down-${s.key}`}
                      className="p-1.5 text-slate-400 hover:text-slate-900 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={() => { setEditing(s); setForm({ ...s }); setOpen(true); }} data-testid={`edit-stage-${s.key}`}
                      className="p-1.5 text-slate-400 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                    {!s.locked && (
                      <button onClick={() => del(s)} data-testid={`delete-stage-${s.key}`}
                        className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_stage")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("stage_label_id")}</Label>
              <Input data-testid="stage-label-id-input" value={form.label_id} onChange={(e) => setForm({ ...form, label_id: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("stage_label_en")}</Label>
              <Input data-testid="stage-label-en-input" value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("probability")} %</Label>
                <Input type="number" min={0} max={100} data-testid="stage-probability-input"
                  value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t("stage_color")}</Label>
                <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                  <SelectTrigger data-testid="stage-color-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGE_COLORS.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span className={`stage-dot ${STAGE_COLOR_CLASSES[c].dot} mr-2`}></span>{c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="stage-active-checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-stage-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StagesAdmin;
